import { useEffect, useRef, useState } from 'react';
import { Banner, BlockStack, Button, Modal, RadioButton, TextField } from './admin-ui';
import { save_pixel } from '../api';
import { normalizeVendorCategoryMappings, pixelError, pixelResponse, pixelValidationError } from '../utils/pixelsAdmin';
import { notifySaveSuccess } from '../utils/saveNotification';
import IntegrationHelpLink from './IntegrationHelpLink';

const t = (text) => text;


const draftFor = (pixel) => ({ ...(pixel || {}), platform: 'applovin', pixel_id: pixel?.pixel_id || '',
  access_token: '', has_access_token: Boolean(pixel?.has_access_token), tracking_owner: pixel?.tracking_owner ?? 1,
  vendor_category_mappings: pixel?.vendor_category_mappings?.length
    ? pixel.vendor_category_mappings.map((mapping) => ({ ...mapping })) : [{ vendor: '', applovin_category_id: '' }] });

export default function AppLovinSettings({ pixel, onChange }) {
  const [draft, setDraft] = useState(() => draftFor(pixel));
  const [open, setOpen] = useState(false);
  const [advanced, setAdvanced] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [disconnecting, setDisconnecting] = useState(false);
  const saving = useRef(false);
  useEffect(() => { setDraft(draftFor(pixel)); setError(''); }, [pixel]);
  const keyChanged = Boolean(pixel && draft.pixel_id.trim().toLowerCase() !== pixel.pixel_id.toLowerCase());

  function updateMapping(index, field, value) {
    setDraft((current) => ({ ...current, vendor_category_mappings: current.vendor_category_mappings.map((mapping, position) =>
      position === index ? { ...mapping, [field]: value } : mapping) }));
  }

  async function save(remove = false) {
    if (saving.current) return;
    setError('');
    if (!remove) {
      const problem = pixelValidationError(draft);
      if (problem) { setError(problem); return; }
    }
    saving.current = true; setBusy(true);
    try {
      const result = pixelResponse(await save_pixel(remove ? { id: pixel.id, platform: 'applovin', is_delete: 1 } : {
        ...(pixel?.id ? { id: pixel.id } : {}), platform: 'applovin', pixel_id: draft.pixel_id.trim().toLowerCase(),
        tracking_owner: Number(draft.tracking_owner),
        vendor_category_mappings: normalizeVendorCategoryMappings(draft.vendor_category_mappings).map((mapping) => ({
          ...mapping, applovin_category_id: Number(mapping.applovin_category_id),
        })),
        ...(draft.access_token.trim() ? { access_token: draft.access_token.trim() } : {}),
      }));
      onChange(remove ? null : result.pixel);
      setDraft(draftFor(remove ? null : result.pixel));
      setDisconnecting(false);
      if (!remove) notifySaveSuccess();
    } catch (failure) { setError(pixelError(failure)); setDisconnecting(false); }
    finally { saving.current = false; setBusy(false); }
  }

  return <details className="pixels-card pixels-applovin" open={open}>
    <summary onClick={(event) => { event.preventDefault(); setOpen(!open); }}>
      <span>AppLovin <small>{t('Optional')}</small></span><span aria-hidden="true">{open ? '−' : '+'}</span>
    </summary>
    <div className="pixels-applovin-content">
      {error && <Banner tone="critical">{t(error)}</Banner>}
      <IntegrationHelpLink article="applovin-pixel" />
      <div className="pixels-applovin-fields">
        <TextField label={t('Event Key')} value={draft.pixel_id} onChange={(value) => setDraft({ ...draft, pixel_id: value })}
          disabled={busy} autoComplete="off" maxLength={36} helpText={t('Enables browser tracking for this store.')} />
        <TextField label={t('Conversion API key (optional)')} type="password" value={draft.access_token}
          onChange={(value) => setDraft({ ...draft, access_token: value })} disabled={busy} autoComplete="new-password"
          helpText={keyChanged && pixel?.has_access_token ? t('The previous key will be cleared. Leave blank to use browser tracking only.') : !keyChanged && pixel?.has_access_token
            ? t('A key is saved. Leave this blank to keep it.') : t('Adds server-side purchase tracking. Reporting Keys are not supported.')} />
      </div>
      <details className="pixels-applovin-advanced" open={advanced}>
        <summary onClick={(event) => { event.preventDefault(); setAdvanced(!advanced); }}>{t('Advanced settings')}</summary>
        <div className="pixels-category-mappings">
          <div className="pixels-category-heading">
            <h3>{t('Product categories')}</h3>
            <a href="https://support.applovin.com/en/growth/promoting-your-websites/track-and-optimize/events-and-objects#category-ids" target="_blank" rel="noreferrer">{t('View AppLovin categories')}</a>
          </div>
          <p>{t('Enter the Vendor exactly as shown in Shopify, including capitalization. All products with that vendor use the same category.')}</p>
          <p>{t('These mappings apply to events sent by this app. Products without a matching vendor are sent without a category ID.')}</p>
          <div className="pixels-category-rows">
            {draft.vendor_category_mappings.map((mapping, index) => <div className="pixels-category-row" key={index}>
              <TextField label={t('Vendor')} labelHidden={index > 0} value={mapping.vendor} autoComplete="off" maxLength={255}
                placeholder={t('Shopify product vendor')} disabled={busy} onChange={(value) => updateMapping(index, 'vendor', value)} />
              <TextField label={t('AppLovin category ID')} labelHidden={index > 0} value={mapping.applovin_category_id} autoComplete="off" maxLength={10}
                placeholder={t('Category ID')} disabled={busy} onChange={(value) => updateMapping(index, 'applovin_category_id', value)} />
              <Button accessibilityLabel={`${t('Remove mapping')} ${index + 1}`} disabled={busy} variant="plain"
                onClick={() => setDraft((current) => ({ ...current, vendor_category_mappings: current.vendor_category_mappings.filter((_, position) => position !== index) }))}>−</Button>
            </div>)}
          </div>
          <Button variant="plain" disabled={busy || draft.vendor_category_mappings.length >= 100}
            onClick={() => setDraft((current) => ({ ...current, vendor_category_mappings: [...current.vendor_category_mappings, { vendor: '', applovin_category_id: '' }] }))}>{t('Add mapping')}</Button>
        </div>
      </details>
      <div className="pixels-applovin-owner">
        <h3>{t('Shopify app + custom COD')}</h3>
        <p>{t('Select who handles page views, product views and cart events.')}</p>
        <BlockStack gap="300">
          <RadioButton name="applovin-owner" label={<span className="pixels-owner-copy"><strong>{t('AppLovin Shopify app')}</strong><small>{t('Recommended for your Shopify storefront. Checkout adds custom checkout and purchase events.')}</small></span>} checked={Number(draft.tracking_owner) === 1}
            onChange={() => setDraft({ ...draft, tracking_owner: 1 })} disabled={busy} />
          <RadioButton name="applovin-owner" label={<span className="pixels-owner-copy"><strong>{t('This app')}</strong><small>{t('For supported custom storefronts where Shopify app tracking is not active. Checkout sends all five events.')}</small></span>} checked={Number(draft.tracking_owner) === 0}
            onChange={() => setDraft({ ...draft, tracking_owner: 0 })} disabled={busy} />
        </BlockStack>
        <p>{t('Changing this setting does not enable or disable the official Shopify app. Confirm which pages it covers before switching.')}</p>
      </div>
      <div className="pixels-actions pixels-applovin-actions">
        <Button variant="primary" loading={busy} onClick={() => save()}>{t('Save AppLovin')}</Button>
        <Button disabled={busy} onClick={() => { setDraft(draftFor(pixel)); setError(''); }}>{t('Cancel')}</Button>
        {pixel && <Button variant="plain" tone="critical" disabled={busy} onClick={() => setDisconnecting(true)}>{t('Disconnect')}</Button>}
      </div>
    </div>
    <Modal open={disconnecting} title={t('Disconnect AppLovin?')} onClose={() => { if (!busy) setDisconnecting(false); }}
      primaryAction={{ content: t('Disconnect'), destructive: true, loading: busy, onAction: () => save(true) }}
      secondaryActions={[{ content: t('Cancel'), disabled: busy, onAction: () => setDisconnecting(false) }]}>
      <Modal.Section>{t('This app will stop sending new AppLovin events. The official Shopify app is not changed.')}</Modal.Section>
    </Modal>
  </details>;
}
